/**
 * Add a team member.
 *
 * There is no public signup anywhere in this service: an account exists because
 * a superadmin issued an invite, or because the `create-superadmin` CLI
 * bootstrapped the first one. So this form is the entire front door, and the
 * token it produces is the whole key to it.
 *
 * `POST /api/v1/admin/invites` stores only `sha256(token)` and returns the
 * plaintext once. Nothing here emails it — the backend has no mailer — which
 * makes the copy step load-bearing rather than a convenience: if the admin
 * closes the panel without copying, the invitee can never register and the only
 * remedy is to issue a second invite. Hence the once-only warning sits beside
 * the value, and the panel stays until it is explicitly dismissed.
 */
import { useState } from 'react'
import type { FormEvent } from 'react'

import { ApiError, api } from '../api/client'
import { ROLES, ROLE_LABEL_KEYS } from '../api/types'
import type { InviteResponse, RoleId } from '../api/types'
import { INPUT } from '../auth/LoginPage'
import { formatPacific, useI18n } from '../i18n'
import type { TFunction } from '../i18n'
import { SecretField, SecretReveal } from './SecretReveal'

/**
 * The SPA route that redeems an invite (`App.tsx` -> `InviteAcceptPage`), which
 * posts the token to `POST /auth/invite/accept`.
 *
 * Exported because it is a promise to the router: change one and the invite
 * URLs already sent out stop resolving. `App.test.tsx` renders the real router
 * at the URL this file builds, so the promise is checked rather than assumed.
 */
export const INVITE_PATH = '/invite'

/**
 * The link the recipient opens.
 *
 * Built from `window.location.origin` rather than a compile-time constant: the
 * same bundle is served from localhost in development and from the monitor
 * domain in production, and an invite that points at the wrong one is
 * indistinguishable from a broken invite.
 *
 * The token goes in the **fragment**, not the query string. It is the sole
 * credential for creating an account at a role this form chose — a superadmin
 * invite is a superadmin bootstrap in a URL — and a query string is written to
 * the reverse proxy's access log, kept in browser history, and carried into
 * whatever chat or ticket the link is pasted into. Only a SHA-256 hash exists
 * server-side, so a plaintext token sitting in a logfile is a standing
 * account-creation capability until the 72h TTL runs out. Fragments are never
 * sent to the server at all.
 *
 * `encodeURIComponent` matters because `token_urlsafe` output is base64url — no
 * `+` or `/` — but a future token format that is not would otherwise be
 * misparsed on the way back out.
 */
export function inviteUrl(token: string): string {
  return `${window.location.origin}${INVITE_PATH}#token=${encodeURIComponent(token)}`
}

export interface InviteMemberFormProps {
  /** An invite may have been accepted since the list was fetched. */
  onInvited?: (invite: InviteResponse) => void
}

export function InviteMemberForm({ onInvited }: InviteMemberFormProps) {
  const { lang, t } = useI18n()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<RoleId>('member')
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [invite, setInvite] = useState<InviteResponse | null>(null)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (pending) return
    setPending(true)
    setError(null)
    try {
      const created = await api.post<InviteResponse>('/api/v1/admin/invites', { email, role })
      setInvite(created)
      // Cleared so the next invite starts from an empty field rather than
      // re-submitting the address that was just used (a guaranteed 409).
      setEmail('')
      setRole('member')
      onInvited?.(created)
    } catch (err) {
      setError(describeAdminError(err, t))
    } finally {
      setPending(false)
    }
  }

  return (
    <section className="space-y-3 rounded-lg border border-border bg-surface p-4">
      <header className="space-y-1">
        <h2 className="text-sm font-semibold text-fg">{t('account.invite.title')}</h2>
        <p className="text-xs text-muted">{t('account.invite.lede')}</p>
      </header>

      <form onSubmit={submit} noValidate className="flex flex-wrap items-end gap-3">
        <div className="min-w-[16rem] flex-1 space-y-1">
          <label htmlFor="invite-email" className="block text-sm font-medium text-fg">
            {t('account.invite.email')}
          </label>
          <input
            id="invite-email"
            type="email"
            name="email"
            autoComplete="off"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={INPUT}
          />
        </div>

        <div className="w-40 space-y-1">
          <label htmlFor="invite-role" className="block text-sm font-medium text-fg">
            {t('account.invite.role')}
          </label>
          <select
            id="invite-role"
            name="role"
            value={role}
            onChange={(e) => setRole(e.target.value as RoleId)}
            className={INPUT}
          >
            {ROLES.map((id) => (
              <option key={id} value={id}>
                {t(ROLE_LABEL_KEYS[id])}
              </option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          disabled={pending}
          aria-busy={pending}
          className="rounded-md bg-accent px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
        >
          {t('account.invite.submit')}
        </button>
      </form>

      {error && (
        <p role="alert" className="rounded-md bg-down/10 px-3 py-2 text-sm text-down">
          {error}
        </p>
      )}

      {invite && (
        <SecretReveal
          title={t('account.invite.reveal.title', { email: invite.email })}
          warning={t('account.invite.reveal.warning')}
          onDismiss={() => setInvite(null)}
        >
          <SecretField
            label={t('account.invite.linkLabel')}
            value={inviteUrl(invite.invite_token)}
            // Both halves follow the reader. `formatPacific` because a bare
            // `toLocaleString()` follows the BROWSER, so a Chinese session on
            // an en-US machine printed a US date — and in the viewer's zone,
            // while every other timestamp in the product is Pacific. The role
            // because it is product vocabulary (成员 / 超级管理员), the same
            // word the picker above and the team table below now show; an id
            // this build does not know still prints verbatim.
            hint={t('account.invite.linkHint', {
              expires:
                formatPacific(invite.expires_at, lang) ?? invite.expires_at,
              role: ROLE_LABEL_KEYS[invite.role as RoleId]
                ? t(ROLE_LABEL_KEYS[invite.role as RoleId])
                : invite.role,
            })}
          />
          <SecretField
            label={t('account.invite.tokenLabel')}
            value={invite.invite_token}
            hint={t('account.invite.tokenHint')}
          />
        </SecretReveal>
      )}
    </section>
  )
}

/**
 * Show what the server said, not a euphemism for it.
 *
 * Every refusal on the admin surface is a specific, actionable sentence —
 * "account already exists", "cannot deactivate the last active superadmin" —
 * and replacing those with "Something went wrong" would hide the one thing the
 * admin needs in order to decide what to do instead.
 */
export function describeAdminError(err: unknown, t: TFunction): string {
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return t('account.adminError.forbidden')
    }
    if (err.status === 422) return t('account.adminError.invalid')
    // `detail` is only a sentence when the responder was FastAPI. `readDetail`
    // (api/client.ts) falls back to the raw response body for anything that is
    // not JSON with a `detail` key, so a 502 error page from Caddy while the api
    // container restarts would otherwise drop a whole HTML document into the
    // team table. Gated on the shape of the body rather than on the status:
    // "database is locked" is a 500 whose own words are the only thing that says
    // what to do next, and throwing those away to catch a proxy page loses more
    // than it saves.
    if (err.detail.length > MAX_DETAIL_CHARS || /^\s*</.test(err.detail)) {
      return t('account.adminError.server', { status: err.status })
    }
    return err.detail || t('account.adminError.failed', { status: err.status })
  }
  return t('account.adminError.network')
}

/** Longer than every sentence the backend writes, shorter than a stack trace. */
const MAX_DETAIL_CHARS = 200
